/**
 * POS Access Levels Configuration
 *
 * Defines what each access level can do in the Peace of Sleep hotel portal.
 * Used by POSContext and POS pages to control UI visibility and actions.
 *
 * Access levels are stored in hosp_property_managers.access_level
 */

interface AccessLevelCapabilities {
  label: string;
  description: string;
  canManageManagers: boolean;
  canEditProperty: boolean;
  canEditRooms: boolean;
  canRequestService: boolean;
  canCancelService: boolean;
  canViewReports: boolean;
}

type AccessLevel = 'owner' | 'manager' | 'viewer';

export const POS_ACCESS_LEVELS: Record<AccessLevel, AccessLevelCapabilities> = {
  owner: {
    label: 'Owner',
    description: 'Full property control including manager management',
    canManageManagers: true,
    canEditProperty: true,
    canEditRooms: true,
    canRequestService: true,
    canCancelService: true,
    canViewReports: true,
  },
  manager: {
    label: 'Manager',
    description: 'Day-to-day operations, cannot manage other managers',
    canManageManagers: false,
    canEditProperty: false,
    canEditRooms: true,
    canRequestService: true,
    canCancelService: true,
    canViewReports: true,
  },
  viewer: {
    label: 'Viewer',
    description: 'Read-only access to all data',
    canManageManagers: false,
    canEditProperty: false,
    canEditRooms: false,
    canRequestService: false,
    canCancelService: false,
    canViewReports: true,
  }
};

/**
 * Get capabilities for a given access level
 */
export function getAccessLevelCapabilities(accessLevel: string): AccessLevelCapabilities {
  return POS_ACCESS_LEVELS[accessLevel as AccessLevel] || POS_ACCESS_LEVELS.viewer;
}

/**
 * Check if an access level can perform a specific action
 */
export function canPerform(accessLevel: string, capability: keyof AccessLevelCapabilities): boolean {
  const caps = getAccessLevelCapabilities(accessLevel);
  return caps[capability] === true;
}

/**
 * Get all access levels for dropdown/radio options
 */
export function getAccessLevelOptions(): Array<{ value: string; label: string; description: string }> {
  return Object.entries(POS_ACCESS_LEVELS).map(([value, config]) => ({
    value,
    label: config.label,
    description: config.description
  }));
}

/**
 * Permissions matrix for reference:
 *
 * | Action                     | Owner | Manager | Viewer |
 * |----------------------------|:-----:|:-------:|:------:|
 * | View dashboard & stats     |   Y   |    Y    |   Y    |
 * | View notifications         |   Y   |    Y    |   Y    |
 * | View service bookings      |   Y   |    Y    |   Y    |
 * | Request new service        |   Y   |    Y    |   N    |
 * | Cancel/reschedule booking  |   Y   |    Y    |   N    |
 * | View rooms                 |   Y   |    Y    |   Y    |
 * | Add/edit/delete rooms      |   Y   |    Y    |   N    |
 * | View property info         |   Y   |    Y    |   Y    |
 * | Edit property contact info |   Y   |    N    |   N    |
 * | Edit customer/contract     |   N   |    N    |   N    |
 * | View managers              |   Y   |    Y    |   Y    |
 * | Add/remove managers        |   Y   |    N    |   N    |
 * | Change access levels       |   Y   |    N    |   N    |
 * | View reports               |   Y   |    Y    |   Y    |
 *
 * Note: Customer and contract details are CMB-managed only.
 */
